import React from 'react';
import { useMemo, useState } from 'react';
import { Dropdown, DropdownItem, FormLabel } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../store';

import { setParams } from '../reducers/paramsReducer';
import { analytics } from '../utils/analytics';
import { BarLoader } from 'react-spinners';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBolt,
  faClock,
  faEnvelope,
  faFire,
  faHourglassHalf,
  faStopwatch,
} from '@fortawesome/free-solid-svg-icons';

// Speed categories in clock order, by the SpeedClass names the backend parses. Each exact time control
// is filed under one, so the dropdown picks either a whole speed or one control.
const SPEEDS = ['UltraBullet', 'Bullet', 'Blitz', 'Rapid', 'Classical', 'Correspondence'] as const;

// A time-control label ("30 sec", "5 min | 3") -> speed, mirroring TimeControlClass.Classify (base +
// 40·increment). Labels come from GamesService.TimeControlMappings; anything unrecognised is null
// ("Other").
export function speedOfLabel(label: string): string | null {
  const m = /^(\d+)\s*(sec|min)(?:\s*\|\s*(\d+))?$/i.exec(label.trim());
  if (m == null) {
    return null;
  }
  const base = parseInt(m[1], 10) * (m[2].toLowerCase() === 'min' ? 60 : 1);
  const inc = m[3] != null ? parseInt(m[3], 10) : 0;
  const estimate = base + 40 * inc;
  if (estimate < 30) return 'UltraBullet';
  if (estimate < 180) return 'Bullet';
  if (estimate < 480) return 'Blitz';
  if (estimate < 1500) return 'Rapid';
  return 'Classical';
}

const SPEED_ICONS: Record<string, IconDefinition> = {
  UltraBullet: faBolt,
  Bullet: faBolt,
  Blitz: faFire,
  Rapid: faStopwatch,
  Classical: faHourglassHalf,
  Correspondence: faEnvelope,
};

// How a time control is shown wherever it appears: an icon and a colour class (`tc-blitz`…) by speed.
export function speedLook(label: string): {
  speed: string | null;
  icon: IconDefinition;
  cls: string;
} {
  const speed = speedOfLabel(label);
  return {
    speed,
    icon: speed != null ? SPEED_ICONS[speed] : faClock,
    cls: `tc-${(speed ?? 'other').toLowerCase()}`,
  };
}

export default function GamesFiltersTimeControls(): React.ReactNode {
  const { timeControl, speed } = useAppSelector((state) => state.paramsReducer.params);
  const { filters, loading } = useAppSelector((state) => state.filtersReducer);

  const dispatch = useAppDispatch();
  const [show, setShow] = useState(false);

  // Exact controls bucketed by speed, keeping the server's order. "Other" collects the unplaceable.
  const grouped = useMemo(() => {
    const buckets = new Map<string, string[]>();
    for (const tc of filters?.TimeControls ?? []) {
      const key = speedOfLabel(tc) ?? 'Other';
      const list = buckets.get(key) ?? [];
      list.push(tc);
      buckets.set(key, list);
    }
    return buckets;
  }, [filters?.TimeControls]);

  // Toggle label: the exact control wins, then the speed, then "All".
  const label = timeControl ?? speed ?? 'All';

  const pickSpeed = (value: string) => {
    // Speed and exact time control are mutually exclusive selections.
    dispatch(setParams({ speed: value, timeControl: null }));
    analytics.track('select_speed', { speed: value, ui_source: 'spa_filter' });
  };
  const pickTimeControl = (value: string) => {
    dispatch(setParams({ timeControl: value, speed: null }));
  };

  return (
    <>
      <FormLabel>Time control</FormLabel>
      <Dropdown onToggle={(nextShow) => setShow(nextShow)}>
        <Dropdown.Toggle className="cm-filter-select">{label}</Dropdown.Toggle>
        <Dropdown.Menu className="cm-filter-menu" show={show}>
          {loading ? (
            <Dropdown.Item disabled>
              <BarLoader color="#fff" />
            </Dropdown.Item>
          ) : null}
          <DropdownItem
            active={timeControl == null && speed == null}
            onClick={() => dispatch(setParams({ timeControl: null, speed: null }))}
          >
            All
          </DropdownItem>
          {SPEEDS.map((s) => {
            const controls = grouped.get(s) ?? [];
            return (
              <React.Fragment key={s}>
                {/* The speed row itself is selectable: every game in that category. */}
                <Dropdown.Item
                  className="cm-tc-speed"
                  active={speed === s && timeControl == null}
                  onClick={() => pickSpeed(s)}
                >
                  {s}
                </Dropdown.Item>
                {controls.map((tc) => (
                  <Dropdown.Item
                    key={tc}
                    className="cm-tc-control"
                    active={timeControl === tc}
                    onClick={() => pickTimeControl(tc)}
                  >
                    {tc}
                  </Dropdown.Item>
                ))}
              </React.Fragment>
            );
          })}
          {(grouped.get('Other') ?? []).length > 0 ? (
            <>
              <Dropdown.Item className="cm-tc-speed" disabled>
                Other
              </Dropdown.Item>
              {(grouped.get('Other') ?? []).map((tc) => (
                <Dropdown.Item
                  key={tc}
                  className="cm-tc-control"
                  active={timeControl === tc}
                  onClick={() => pickTimeControl(tc)}
                >
                  {tc}
                </Dropdown.Item>
              ))}
            </>
          ) : null}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
}
